/*!
 * Copyright (C) Octillion Media LLC 2019
 * All Rights Reserved.
 */
export const namespaced = true;
import { toastMsg } from '@/helpers/util';
import { getSports, getLeagues, getTeams, getGames, getCustomListApi, getCampaigns, getSportCampaignSingleDataApi } from '../../helpers/api';
export const state = {
  campaigns: [],
  campaignCurrentStep: 'sport',
  sports: [],
  leagues: [],
  teams: [],
  games: [],
  selectedSport: null,
  selectedLeagues: [],
  selectedTeams: [],
  selectedGames: [],
  selectedLiveEvents: [],
  selectedCreatives: [],
  campaignForm: {
    campaign_name: null,
    max_cpm: null,
    advertiser: null,
    budget: null,
    start_date: null,
    end_date: null,
    zipcodes: [],
    agreed: false,
    live_events: true,
    delivery_mode: 'DELIVER_ASAP'
  },
  agreedError: false,
  zipcodes: [],
  selectedZipcodes: [],
  campaignMode: 'create', // 'create' or 'edit',
  campaignLoader: false,
  campaignOnSubmitLoader: false,
  campaignSingleErr: false,
  loader: {
    teams: false,
    leagues: false,
    games: false
  },
  teamsRequestKey: 0,
  campaignStatus: null // 'draft', 'active', 'paused', 'completed'
}

export const getters = {
    zipcodes: state => state.zipcodes,
    advertiser_id: state => state.campaignForm?.advertiser?.advertiser_id || null,
    loader: state => state.loader,
    campaignMode: state => state.campaignMode,
    campaignStatus: state => state.campaignStatus,
    campaignOnSubmitLoader: state => state.campaignOnSubmitLoader
};

export const mutations = {
  campaign_current_step(state, payload) {
    state.campaignCurrentStep = payload;
  },
  UPDATE_CAMPAIGN_FORM(state, payload) {
    state.campaignForm = {
      ...state.campaignForm,
      ...payload
    };
  },
  SET_CAMPAIGN_AGREED_ERROR(state, payload) {
    state.agreedError = payload;
  },
  selected_sport(state, payload) {
    state.selectedSport = payload;
  },
  sports(state, payload) {
    state.sports = payload;
  },
  leagues(state, payload) {
    state.leagues = payload;
  },  
  teams(state, payload) {
    state.teams = payload;
  },
  setTeamsRequestKey(state, key) {
    state.teamsRequestKey = key;
  },
  games(state, payload) {
    state.games = payload;
  },
  selected_leagues(state, payload) {
    state.selectedLeagues = payload;
  },
  selected_teams(state, payload) {
    state.selectedTeams = payload;
  },
  selected_games(state, payload) {
    state.selectedGames = payload;
  },
  selected_live_events(state, payload) {
    state.selectedLiveEvents = payload;
  },
  selected_creatives(state, payload) {
    state.selectedCreatives = payload;
  },
  reset_campaign(state, payload) {
    state.campaignCurrentStep = payload.campaignCurrentStep
    state.selectedSport = null
    state.selectedLeagues = []
    state.selectedTeams = []
    state.selectedGames = []
    state.selectedLiveEvents = []
    state.selectedCreatives = []
    state.campaignForm = {
      campaign_name: null,
      max_cpm: null,
      advertiser: null,
      start_date: null,
      end_date: null,
      zipcodes: [],
      agreed: false,
      live_events: 'live_event_and_replay',
      delivery_mode: 'DELIVER_ASAP'
    },
    state.campaignSingleErr = false;
  },
  clear_campaign(state) {
    state.selectedLeagues = []
    state.selectedTeams = []
    state.selectedGames = []
    state.selectedLiveEvents = []
    state.selectedCreatives = []
    state.campaignForm = {
      campaign_name: null,
      max_cpm: null,
      advertiser: null,
      start_date: null,
      end_date: null,
      zipcodes: [],
      agreed: false,
      live_events: 'live_event_and_replay',
      delivery_mode: 'DELIVER_ASAP'
    },
    state.campaignSingleErr = false;
    state.campaignStatus = null
  },
  setZipcodes(state, newValue) {
    state.zipcodes = newValue
  },
  SET_SELECTED_ZIPCODE(state, zipcodes) {
    state.selectedZipcodes = zipcodes;
  },
  ADD_SELECTED_ZIPCODE(state, zipcode) {
    const exists = state.selectedZipcodes.some(loc => loc.list_id === zipcode.list_id);
    if (!exists) {
      state.selectedZipcodes.push(zipcode);
    }
  },
  REMOVE_SELECTED_ZIPCODE(state, zipcodeValue) {
    state.selectedZipcodes = state.selectedZipcodes.filter(
      loc => loc.list_id !== zipcodeValue.list_id
    );
  },
  CLEAR_ZIPCODE(state) {
    state.selectedZipcodes = [];
  },
  setCampaigns(state, newValue) {
    state.campaigns = newValue
  },
  setCampaignMode(state, mode) {  
    state.campaignMode = mode;
  },
  setSingleCampaign(state, campaign) {
    state.campaignForm = {
      campaign_name: campaign?.name,
      max_cpm: campaign?.max_cpm,
      advertiser: campaign?.advertiser_id ? {
        advertiser_id: campaign.advertiser_id ,
        advertiser_name: campaign.advertiser_name,
        display_name: campaign.advertiser_name || campaign.advertiser_id
      } : null,
      budget: campaign?.budget,
      start_date: campaign?.start_date,
      end_date: campaign?.end_date,
      zipcodes: campaign?.zipcodes ? campaign?.zipcodes?.split(',') : [],
      agreed: campaign?.disclaimer_check,
      live_events: campaign?.delivery_window,
      delivery_mode: campaign?.bidding_strategy
    };
    state.selectedSport = campaign?.sport_detail ? campaign?.sport_detail : Array.isArray(campaign?.live_sports) && campaign.live_sports.length > 0 ? campaign.live_sports[0] : null;
    state.selectedLeagues = campaign?.live_sports_leagues || [];
    state.selectedTeams = campaign?.live_sports_teams || [];
    state.selectedGames = campaign?.live_games || [];
    state.selectedCreatives = campaign?.creatives_list || [];
    state.campaignStatus = campaign?.status || 'draft';
    state.agreedError = false;
  },
  setCampaignLoader(state, payload) {
    state.campaignLoader = payload;
  },
  setCampaignOnSubmitLoader(state, payload) {
    state.campaignOnSubmitLoader = payload;
  },
  setCampaignSingleErr(state, payload) {
    state.campaignSingleErr = payload;
  },
  setLoader(state, { key, value }) {
    if (Object.prototype.hasOwnProperty.call(state.loader, key)) {
      state.loader[key] = value;
    }
  }
}

export const actions = {
  setCampaignMode({ commit }, mode) {
    commit('setCampaignMode', mode);
  },
  setCampaignOnSubmitLoader({ commit }, payload) {
    commit('setCampaignOnSubmitLoader', payload);
  },
  setCampaignStep({ commit }, payload) {
    commit('campaign_current_step', payload);
  },
  fetchCampaigns({commit}, payload) {
      getCampaigns(payload).then(res => {
          let result = res.data;
          if (payload.version) {
              result.version = payload.version;
          }
          commit('setCampaigns', result.campaigns_list)
      }, (error) => {
          commit('setCampaignErr', error.data)
      })
  },
  fetchSingleCampaign({commit}, payload) {  
    commit('setCampaignLoader', true);
    commit('setCampaignSingleErr', false);
    getSportCampaignSingleDataApi(payload.id, payload.advertiser_id).then(res => {
      let result = res.data;
      if(result.campaign) {
        commit('setSingleCampaign', result.campaign);
        commit('location/SET_SELECTED_LOCATION', result.campaign.locations || [], { root: true });
        commit('setCampaignMode', 'edit');
      }
      commit('setCampaignLoader', false);
      commit('setCampaignSingleErr', false);
    }, (error)=> {
      commit('setCampaignSingleErr', true);
      commit('setCampaignLoader', false);
      toastMsg("Unable to retrieve campaign details. Please try again later.", "error");
    });
  },
  onChangeSport({ commit }, payload) {
    commit('selected_sport', payload);
    commit('clear_campaign');
  },
  resetCampaign({ commit }, payload = {campaignCurrentStep: 'sport'}) {
    commit('reset_campaign', payload);
  },
  setSelectedLeagues({ commit }, payload) {
    commit('selected_leagues', payload);
  },
  setSelectedTeams({ commit }, payload) {
    commit('selected_teams', payload);
  },
  setSelectedGames({ commit }, payload) {
    commit('selected_games', payload);
  },
  setSelectedLiveEvents({ commit }, payload) {
    commit('selected_live_events', payload);
  },
  setSelectedCreatives({ commit }, payload) {
    commit('selected_creatives', payload);
  },
  updateCampaignForm({ commit }, payload) {
    commit('UPDATE_CAMPAIGN_FORM', payload);
  },
  setCampaignAgreedError({ commit }, payload) {
    commit('SET_CAMPAIGN_AGREED_ERROR', payload);
  },
  fetchSports({ commit }, payload) {
    getSports(payload)
      .then((res) => {
        if (res && res.data && res.data.content) {
          // let sportsData = res.data.content.map((item) => {
          //   // if the title is "All Sports", set the custom id
          //   if (item.title === "All Sports") {
          //     return { ...item, id: "GN3FAZE2HJTVDA9", active:true };
          //   }
          //   return item;
          // });
          // commit("sports", sportsData);
          commit('sports', res.data.content);
        } else {
          commit("sports", []);
        }
      })
      .catch(() => {
        commit("sports", []);
      });
  },
  fetchLeagues({ commit }, payload) {
    commit('setLoader', { key: 'leagues', value: true });
    getLeagues(payload).then(res => {
      if(res && res.data && res.data.content) {
        commit('leagues', res.data.content);
      } else {
        commit('leagues', []);
      }
      commit('setLoader', { key: 'leagues', value: false });
    }).catch(() => {
      commit('leagues', []);
      commit('setLoader', { key: 'leagues', value: false });
    })
  },
  fetchTeams({ commit, state }, payload) {
    commit('setLoader', { key: 'teams', value: true });

    // Support optional requestKey so callers can ignore stale responses
    const requestKey = payload?.requestKey;
    if (requestKey !== undefined) {
      commit('setTeamsRequestKey', requestKey);
    }

    // build payload without requestKey for API call
    const payloadForApi = { ...(payload || {}) };
    if (payloadForApi.requestKey !== undefined) delete payloadForApi.requestKey;

    getTeams(payloadForApi).then(res => {
      // if a requestKey was provided and doesn't match latest, ignore this result
      if (requestKey !== undefined && requestKey !== state.teamsRequestKey) {
        commit('setLoader', { key: 'teams', value: false });
        return;
      }

      if(res && res.data && res.data.content) {
        commit('teams', res.data.content);
      } else {
        commit('teams', []);
      }
      commit('setLoader', { key: 'teams', value: false });
    }).catch(() => {
      commit('teams', []);
      commit('setLoader', { key: 'teams', value: false });
    })
  },
  fetchGames({ commit }, payload) {
    commit('setLoader', { key: 'games', value: true });
    getGames(payload).then(res => {
      if(res && res.data) {
        commit('games', res.data);
      } else {
        commit('games', []);
      }
      commit('setLoader', { key: 'games', value: false });
    }).catch(() => {
      commit('games', []);
      commit('setLoader', { key: 'games', value: false });
    })
  },
  async getCustomList({ commit }, payload) {
    try {
      const res = await getCustomListApi(payload);
      commit('setZipcodes', res.data);
    } catch (error) {
      commit('setZipcodes', []);
    }
  },
  addSelectedZipcode({ commit }, payload) {
    commit('ADD_SELECTED_ZIPCODE', payload);
  },
  removeSelectedZipcode({ commit }, payload) {
    commit('REMOVE_SELECTED_ZIPCODE', payload);
  },
  clearZipcodeSelected({ commit }) {
    commit('CLEAR_ZIPCODE')
  }
}
